import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  CONTACT_DETAIL_CONTAINER,
  MAIN_CONTAINER,
} from './constants/common.constants';
import { TabService } from './core/services/tab.service';
import { Tab } from './model/common/tab';
import { AuthService } from './services/auth.service';
import { BreadCrumbBuilderService } from './services/builder/breadcrumb-builder.service';
import { ContactApiService } from './services/api/contact-api.service';
import { DeviceService } from './services/device.service';
import { EnvironmentService } from './services/environment.service';
import { FacilityApiService } from './services/api/facility-api.service';
import { LogAndMessageService } from './services/log-and-message.service';
import { MenuBuilderService } from './services/builder/menu-builder.service';
import { AppsyncService } from './services/appsync.service';
import { ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthApiService } from './services/api/auth-api.service';
import { CalendarOptions } from 'fullcalendar';
import dayGridPlugin from '@fullcalendar/angular/daygrid';
import classicThemePlugin from '@fullcalendar/angular/themes/classic';
import { CheckUserExistResponse } from './model/common/check-user-exist-response';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { ThemeService } from './services/theme.service';
import { isNullOrUndefined } from './utils/utils';
import { DetailsChangesEvent } from './core/containers/details-container';
import { Location } from '@angular/common';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { ToolbarComponent } from './common/components/toolbar.component';
import { ProgressSpinnerComponent } from './core/components/progress-spinner.component';
import { TAB_COMPONENTS } from './tabs';
import { PrivacyConsentContainerComponent } from './common/containers/privacy-consent-container.component';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { Button, ButtonModule } from '@openng/optimus-ui/button';
import { Toast } from '@openng/optimus-ui/toast';
import { Message } from '@openng/optimus-ui/message';
import { Drawer } from '@openng/optimus-ui/drawer';
import { AppMenuComponent } from './core/components/menu/app.menu.component';
import { Breadcrumb } from '@openng/optimus-ui/breadcrumb';
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog';
import { Dialog } from '@openng/optimus-ui/dialog';
import { AdminApiService } from './services/api/admin-api.service';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { StorageService } from './services/storage.service';

@Component({
  selector: 'app-root',
  providers: [DynamicDialogConfig, DynamicDialogRef],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Toast,
    ProgressSpinnerComponent,
    Message,
    Drawer,
    ...TAB_COMPONENTS,
    AppMenuComponent,
    ToolbarComponent,
    Breadcrumb,
    Button,
    RouterOutlet,
    ConfirmDialog,
    Dialog,
    PrivacyConsentContainerComponent,
    TranslatePipe,
    TabsModule,
    ButtonModule,
    TooltipModule,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterContentChecked {
  protected _translate = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _environmentService = inject(EnvironmentService);
  protected _router = inject(Router);
  protected _logAndMessageService = inject(LogAndMessageService);
  protected _deviceService = inject(DeviceService);
  protected _appSync = inject(AppsyncService);
  protected _contactApi = inject(ContactApiService);
  protected _facilityApi = inject(FacilityApiService);
  protected _oktaAuth = inject(OktaAuthStateService);
  protected _auth = inject(AuthService);
  protected _authApi = inject(AuthApiService);
  protected _menuBuilder = inject(MenuBuilderService);
  protected _updates = inject(SwUpdate);
  protected _cdref = inject(ChangeDetectorRef);
  protected _titleService = inject(Title);
  protected _themeService = inject(ThemeService);
  protected _location = inject(Location);
  protected _tabService = inject(TabService);
  protected _adminApi = inject(AdminApiService);
  protected _storageService = inject(StorageService);

  menuOpen = false;
  breadCrumbs: any[] = [];
  homeItem: any;

  version: string = '';
  routeLoading = false;
  breadCrumbSub?: Subscription;
  activeMediaQuery = '';
  toolbarTitle: string = '';
  menuItems: MenuItem[] = [];
  authenticated = false;
  showLoadingScreen = false;
  loadingStatus: { user: boolean | null } = { user: null };
  showPrivacyDialog = false;
  deviceInfo: any;
  notSupported = false;
  useSecondary = null;

  openTabs: Tab[] = [];
  activeIndex = 0;
  tabChangeSub?: Subscription;
  revertChangeSub?: Subscription;
  activeTabChangedSub?: Subscription;
  mobileTabChangedSub?: Subscription;
  mobileTab: any;
  isInitialized = false;
  isAuthenticated = false;

  existingContact: boolean = false;
  consentGivenAt?: Date;
  user?: CheckUserExistResponse;

  facilitiesCacheInterval?: Subscription;
  userClaimsCacheInterval?: Subscription;

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [classicThemePlugin, dayGridPlugin],
  };

  ngOnInit(): void {
    // Set default language
    // Note: Commenting out the 2 lines below causes the app to show the keys instead of the translated value
    // this is useful for finding places where we have hardcoded strings throughout the app.
    this._translate.setFallbackLang('en');

    // Public facility pages bypass authenticated app-shell initialization.
    const isPublicFacilityRoute =
      this._location.path().includes('facility-sign-in') ||
      this._location.path().includes('facility-sign-out');

    // Tab subscriptions must always be set up so that tab events work even when
    // the app is first loaded on a public route and later navigates to an authenticated one.
    this._deviceService.initDeviceInfo();
    this.setupTabsChangedSubscription();
    this.setupMobileTabChangedSubscription();
    this.setupActiveTabChangedSubscription();

    if (!isPublicFacilityRoute) {
      this._oktaAuth.authState$.subscribe((state) => {
        if (state && state.idToken && state.idToken.claims) {
          this._auth.setJWT(state.idToken.idToken);
          this.isAuthenticated = true;
          const userClaims = this._authApi.getUserClaims();
          if (!userClaims || userClaims.length === 0) {
            this.populateUserClaims();
          } else {
            this.initialize();
          }
        }
      });
    } else {
      // When starting on a public route, lazily initialize the authenticated app shell
      // the first time the user navigates to a non-public (authenticated) route.
      this._router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          filter(
            (e: any) =>
              !e.url.includes('facility-sign-in') &&
              !e.url.includes('facility-sign-out')
          ),
          take(1)
        )
        .subscribe(() => {
          this._oktaAuth.authState$.pipe(take(1)).subscribe((state) => {
            if (state && state.idToken && state.idToken.claims) {
              this._auth.setJWT(state.idToken.idToken);
              this.isAuthenticated = true;
              const userClaims = this._authApi.getUserClaims();
              if (!userClaims || userClaims.length === 0) {
                this.populateUserClaims();
              } else {
                this.initialize();
              }
            }
          });
        });
    }

    if (!isPublicFacilityRoute) {
      if (this._deviceService.isIE()) {
        this._logAndMessageService.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.BROWSER_NOT_SUPPORTED',
          bodyKey: 'COMMON.MESSAGES.ERROR.BROWSER_NOT_SUPPORTED_MSG',
        });
        this.notSupported = true;
      } else {
        // Do we have an enabled service worker?  If so then check for updates.
        if (
          this._updates.isEnabled &&
          this._deviceService.isBrowserCacheEnabled()
        ) {
          // This will fire when an SW update is available.  Do some logging and then warn the user a reload is needed.
          this._updates.versionUpdates
            .pipe(
              filter(
                (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'
              ),
              map((evt) => ({
                type: 'UPDATE_AVAILABLE',
                current: evt.currentVersion,
                available: evt.latestVersion,
              }))
            )
            .subscribe(
              (event) => {
                console.log('current version is', event.current);
                console.log('available version is', event.available);
                // TODO PROMPT because this would kill the current session.
                //  this._updates.activateUpdate().then(() => document.location.reload());
                this._logAndMessageService.translateToWarnMessage({
                  headerKey: 'COMMON.MESSAGES.HEADERS.NEW_VERSION',
                  bodyKey: 'COMMON.MESSAGES.WARN.NEW_VERSION_MSG',
                });
              },
              (error) => console.error(error)
            );
        }
      }
    }

    this._titleService.setTitle(`${environment.title}`);
    this.toolbarTitle = this._environmentService.getTitle();
  }

  ngAfterContentChecked() {
    this._cdref.detectChanges();
  }

  isMobile(): boolean {
    return this._deviceService.isMobile();
  }

  populateUserClaims() {
    this.showLoadingScreen = true;
    this._authApi
      .loadUserClaims()
      .pipe(take(1))
      .subscribe((response: any) => {
        let claimResponse = response.data.loadUserClaims;
        if (claimResponse !== null) {
          this._authApi.setUserClaims(claimResponse);
          this.initialize();
        } else {
          this.showLoadingScreen = false;
          this._logAndMessageService.error(
            'User Claims Not Found',
            'User claims unable to be loaded. The user may not exist in the database. Please contact your admin.'
          );
        }
      });
  }

  async initialize() {
    this._themeService.tryRestoreThemeFromStorage();
    await this.tryRestoreLangFromStorage();

    if (!this.isInitialized) {
      this.showLoadingScreen = true;
      this.isInitialized = true;

      if (this._appSync.isClientConfigured()) {
        this.checkUserAndImportIfNotExist();
      } else {
        this._appSync.clientHydratedEvent.pipe(take(1)).subscribe(() => {
          this.checkUserAndImportIfNotExist();
        });
      }
      this.finishInit();
      this.setupApplication();
      const homeHeader = await firstValueFrom(
        this._translate.get('COMMON.LABEL.HOME_HEADER')
      );
      const mainTab = this._tabService.buildNewTab(
        MAIN_CONTAINER,
        false,
        homeHeader,
        undefined,
        'main'
      );
      if (this._deviceService.isMobile()) {
        this._tabService.setMobileTab(mainTab);
      } else {
        this._tabService.openTab(mainTab);
      }
    }
  }

  checkUserAndImportIfNotExist(): void {
    this._contactApi.checkUserExist().subscribe({
      next: (existResult) => {
        this.user = existResult;

        this.showLoadingScreen = false;

        this.existingContact = existResult.existing;
        this.consentGivenAt = existResult.consentGivenAt;

        if (existResult.success === false) {
          this.showLoadingScreen = false;
          this._logAndMessageService.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.VERIFY_USER_FAILED',
            bodyKey: 'COMMON.MESSAGES.ERROR.VERIFY_USER_FAILED_MSG',
          });
        } else if (
          !this.existingContact ||
          isNullOrUndefined(this.consentGivenAt)
        ) {
          // Prompt user to accept privacy policy if the user is new
          // or hasn't agreed to the policy before.
          this.showPrivacyDialog = true;
        } else {
          this.finishInit();
          this.setupApplication();
        }
      },
      error: (err) => {
        this.showLoadingScreen = false;
        console.error(err);

        this._logAndMessageService.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.VERIFY_USER_FAILED',
          bodyKey: 'COMMON.MESSAGES.ERROR.VERIFY_USER_FAILED_MSG',
        });
      },
    });
  }

  setupApplication() {
    this.authenticated = true;
    this.homeItem = {
      icon: 'fa fa-home',
      command: () => {
        this.breadCrumbs = [];
        const tab = this._tabService.buildNewTab(MAIN_CONTAINER, false);
        this._tabService.setMobileTab(tab);
      },
    };
    this.breadCrumbs = [];
    this.setupMenu();
    this.breadCrumbSub =
      this._breadCrumbBuilderService.breadCrumbChangedEvent.subscribe(
        (data) => {
          this.breadCrumbs = data;
        }
      );
    this.version = this._environmentService.getVersion();
  }

  /**
   * Cleanup
   */
  ngOnDestroy() {
    this.breadCrumbSub?.unsubscribe();
    this.facilitiesCacheInterval?.unsubscribe();
    this.userClaimsCacheInterval?.unsubscribe();
    this.tabChangeSub?.unsubscribe();
    this.revertChangeSub?.unsubscribe();
    this.activeTabChangedSub?.unsubscribe();
    this.mobileTabChangedSub?.unsubscribe();
  }

  /**
   * private method used to create menu for menu events.
   */
  private async setupMenu(): Promise<void> {
    this.menuItems = await this._menuBuilder.buildMenu();
  }

  menuToggle() {
    this.menuOpen = !this.menuOpen;
  }

  loadCommonData() {
    // Auto refresh claims cache every 5 minutes
    if (!this.userClaimsCacheInterval) {
      const refreshInterval = interval(5 * 60 * 1000);
      this.userClaimsCacheInterval = refreshInterval.subscribe(() => {
        this._authApi.getUserClaims();
      });
    }

    this._facilityApi.loadFacilities();

    // Auto refresh facilities cache every 5 minutes
    if (!this.facilitiesCacheInterval) {
      const refreshInterval = interval(5 * 60 * 1000);
      this.facilitiesCacheInterval = refreshInterval.subscribe(() => {
        this._facilityApi.loadFacilities();
      });
    }

    this._authApi.loadUserBusinessUnits();
    this._facilityApi.getFacilityTypes().subscribe((data) => {});
    this._adminApi.getCompanies().subscribe((data) => {});
    this._contactApi.getJobRoles().subscribe((data) => {});
  }

  importUser(): void {
    this._contactApi
      .importUser()
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          if (user) {
            this._logAndMessageService.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.USER_IMPORT_SUCCESS',
              bodyKey: 'COMMON.MESSAGES.SUCCESS.USER_IMPORT_SUCCESS_MSG',
            });
            const tab = this._tabService.buildNewTab(
              CONTACT_DETAIL_CONTAINER,
              true,
              undefined,
              user.id
            );
            this._tabService.openTab(tab);
          }
        },
        error: (error) => {
          this._logAndMessageService.translateToWarnMessage({
            headerKey: 'COMMON.MESSAGES.HEADERS.USER_IMPORT_FAILED',
            bodyKey: 'COMMON.MESSAGES.ERROR.USER_IMPORT_FAILED_MSG',
          });
        },
        complete: () => {
          this.finishInit();
        },
      });
  }

  policyAccepted() {
    if (!this.existingContact) {
      this.importUser();
    } else {
      this._contactApi
        .markConsentGivenAt(this.user?.contactId ?? '')
        .pipe(take(1))
        .subscribe({
          next: () => {},
          error: (error) => {
            this._logAndMessageService.errorLogOnly(error);
            this._logAndMessageService.translateToErrorAlert({
              bodyKey: 'COMMON.MESSAGES.ERROR.PRIVACY_POLICY',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
        });
    }
    this.finishInit();
  }

  finishInit(): void {
    this.loadCommonData();
    this.setupApplication();
  }

  setupTabsChangedSubscription(): void {
    this.tabChangeSub = this._tabService.tabsChangedEvent.subscribe((tabs) => {
      this.openTabs = [...tabs];
      this.menuOpen = false;
    });
  }

  setupMobileTabChangedSubscription(): void {
    this.mobileTabChangedSub = this._tabService.mobileTabChanged.subscribe(
      (tab) => {
        this.mobileTab = tab;
        this.menuOpen = false;
      }
    );
  }

  setupActiveTabChangedSubscription(): void {
    // Needed to change the colors of the tabs to indicate the new active one
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        this.activeIndex = this._tabService.getActiveIndex();
      }
    );
  }

  getActiveIndex(): number {
    return this._tabService.getActiveIndex();
  }

  closeTab(i: number): void {
    const closingTab = this._tabService.getTabByIndex(i);
    if (closingTab.index !== this._tabService.getActiveIndex()) {
      this._tabService.setActiveTab(closingTab.index);
    }

    if (!isNullOrUndefined(closingTab) && isNullOrUndefined(closingTab.id)) {
      this._tabService.closeTab(i);
    } else {
      this._tabService.attemptCloseEvent.emit(closingTab);
    }
  }

  onActivate($event: any): void {
    if (!isNullOrUndefined($event) && !isNullOrUndefined($event.routerLink)) {
      this._router.navigate([$event.routerLink]);
    }
  }

  setupRevertChangedSubscription(): void {
    this.revertChangeSub = this._tabService.revertTabChangeEvent.subscribe(
      () => {
        this._tabService.revertToPrevTab();
        this.activeIndex = this._tabService.getActiveIndex();
      }
    );
  }

  handleDetailChange($event: DetailsChangesEvent): void {
    if (!isNullOrUndefined($event)) {
      const tab = this._tabService.getActiveTab();
      if ($event.isDirty === true && !tab.header.includes('*')) {
        this._tabService.updateActiveTabLabel('*' + tab.header);
      } else if ($event.isDirty === false && tab.header.includes('*')) {
        this._tabService.updateActiveTabLabel(tab.header.slice(1));
      }
    }
  }

  changeActiveTab(i: number): void {
    this.activeIndex = i;
    this._tabService.setActiveTab(i);
  }

  onBreadCrumbClick(event: any) {
    const clickedIndex = this.breadCrumbs.findIndex(
      (b) => b.label === event.item.label
    );

    if (clickedIndex !== -1) {
      this.breadCrumbs = this.breadCrumbs.slice(0, clickedIndex + 1);
      this._breadCrumbBuilderService.sendEvent(event);
    }
  }

  popOutFieldVision(): void {
    const fvUrl = this._environmentService.getFieldVisionUrl();
    window.open(fvUrl, '_blank');
  }

  async tryRestoreLangFromStorage() {
    let storedLanguage = await this._storageService.getLang();

    const groups = this._auth.getUserGroups();

    if (storedLanguage) {
      // Use local storage theme first
      this._translate.use(storedLanguage);
    } else if (groups.every((group) => group.includes('MGO'))) {
      // if their groups only contain MGO groups, default to spanish
      this._translate.use('es');
      this._storageService.storeLang('es');
    } else {
      // otherwise default to english
      this._translate.use('en');
      this._storageService.storeLang('en');
    }
  }
}
